import BarChartOutlined from '@ant-design/icons/BarChartOutlined'
import BulbOutlined from '@ant-design/icons/BulbOutlined'
import EllipsisOutlined from '@ant-design/icons/EllipsisOutlined'
import EnvironmentOutlined from '@ant-design/icons/EnvironmentOutlined'
import FilterOutlined from '@ant-design/icons/FilterOutlined'
import NumberOutlined from '@ant-design/icons/NumberOutlined'
import TableOutlined from '@ant-design/icons/TableOutlined'
import { EXPLORER_LIGHT_GREEN } from 'features/drilldown/colors'
import { ReactNode } from 'react'
import CantileverTheme from 'styles/cantileverTheme'
import { DrilldownPanelGroupType, DrilldownPanelType } from 'types'

import { DRILLDOWN_PANEL_CONFIGS, DrilldownPanelConfig } from './PanelConfigs'

type ButtonGroupSpec = {
  name: DrilldownPanelGroupType
  tooltip?: string
  icon?: ReactNode
}

export const BUTTON_GROUP_SPECS: Record<
  DrilldownPanelGroupType,
  ButtonGroupSpec
> = {
  llmPanel: {
    name: 'llmPanel',
    tooltip: 'AI Assistant',
    icon: (
      <BulbOutlined style={{ color: CantileverTheme.colors.purple['300'] }} />
    ),
  },
  counts: {
    name: 'counts',
    tooltip: 'Counts',
    icon: (
      <NumberOutlined style={{ color: CantileverTheme.colors.blue['300'] }} />
    ),
  },
  filter: {
    name: 'filter',
    tooltip: 'Filters',
    icon: (
      <FilterOutlined
        style={{ color: CantileverTheme.colors.crimson['200'] }}
      />
    ),
  },
  table: {
    name: 'table',
    tooltip: 'Tables',
    icon: (
      <TableOutlined style={{ color: CantileverTheme.colors.purple['300'] }} />
    ),
  },
  visualizations: {
    name: 'visualizations',
    tooltip: 'Visualizations',
    icon: <EnvironmentOutlined style={{ color: EXPLORER_LIGHT_GREEN }} />,
  },
  histogram: {
    name: 'histogram',
    tooltip: 'Histograms',
    icon: (
      <BarChartOutlined style={{ color: CantileverTheme.colors.blue['300'] }} />
    ),
  },
  misc: {
    name: 'misc',
    tooltip: 'Miscellaneous',
    icon: (
      <EllipsisOutlined
        style={{ color: CantileverTheme.colors.slate['400'] }}
      />
    ),
  },
}

export const DRILLDOWN_BUTTON_GROUPINGS: Readonly<
  Record<DrilldownPanelGroupType, DrilldownPanelConfig[]>
> = {
  llmPanel: [DRILLDOWN_PANEL_CONFIGS.llmPanel],
  counts: [DRILLDOWN_PANEL_CONFIGS.counts],
  filter: [DRILLDOWN_PANEL_CONFIGS.filter],
  table: [
    DRILLDOWN_PANEL_CONFIGS.siteTable,
    DRILLDOWN_PANEL_CONFIGS.unitTable,
    DRILLDOWN_PANEL_CONFIGS.inspectionTable,
    DRILLDOWN_PANEL_CONFIGS.indicationTable,

    // Impl-specific
    DRILLDOWN_PANEL_CONFIGS.niiTable,
    DRILLDOWN_PANEL_CONFIGS.ffsResultTable,
  ],
  visualizations: [
    DRILLDOWN_PANEL_CONFIGS.siteView,
    DRILLDOWN_PANEL_CONFIGS.map,
    DRILLDOWN_PANEL_CONFIGS.pointCloud,
  ],
  histogram: [
    DRILLDOWN_PANEL_CONFIGS.siteHistogram,
    DRILLDOWN_PANEL_CONFIGS.assetHistogram,
    DRILLDOWN_PANEL_CONFIGS.assetTypeHistogram,
    DRILLDOWN_PANEL_CONFIGS.inspectionTypeHistogram,
    DRILLDOWN_PANEL_CONFIGS.indicationClassificationHistogram,
    DRILLDOWN_PANEL_CONFIGS.indicationDamageMechanismHistogram,
  ],
  misc: [
    DRILLDOWN_PANEL_CONFIGS.columnBreak,
    DRILLDOWN_PANEL_CONFIGS.cards,
    DRILLDOWN_PANEL_CONFIGS.inspectionTimeline,
    DRILLDOWN_PANEL_CONFIGS.markdown,
    DRILLDOWN_PANEL_CONFIGS.indicationList,
    DRILLDOWN_PANEL_CONFIGS.bulkEdit,
    DRILLDOWN_PANEL_CONFIGS.activeFilters,
    DRILLDOWN_PANEL_CONFIGS.metrics,

    // Impl-specific
    DRILLDOWN_PANEL_CONFIGS.rbiMatrix,
  ],
}

/**
 * Map of panel names to their grouping type for looking up the grouping, eg for highlighting a grouping icon.
 * A key assumption is that each panel is in only one group. This may change in the future.
 */

export const DRILLDOWN_PANELS_TO_GROUPING_TYPES = Object.entries(
  DRILLDOWN_BUTTON_GROUPINGS
).reduce(
  (acc, [groupingType, panels]) => {
    return {
      ...acc,
      ...Object.fromEntries(panels.map((panel) => [panel.name, groupingType])),
    }
  },
  {} as Record<DrilldownPanelType, DrilldownPanelGroupType>
)
